
import { entitySlugForLocale, buildHreflangLinks } from '@/utils/hreflang'
import { buildOgLocaleMeta } from '@/utils/seo'
import productMixin from '@/mixins/productMixin.js'
import headlightTypesMixin from '@/mixins/headlightTypesMixin'
import showVideoMixin from '@/mixins/showVideoMixin'
import Vue2Filters from 'vue2-filters'
import {
  colorTemperatureTone,
  formatColorTemperature,
  colorTemperatureWording,
  withoutColorTemperatureSpecifications,
} from '@/utils/colorTemperature'
import { copyText } from '@/utils/copyText'

export default {
  name: 'ProductShow',
  mixins: [
    productMixin,
    headlightTypesMixin,
    showVideoMixin,
    Vue2Filters.mixin,
  ],

  data() {
    return {
      tabIndex: 0,
      popStateEventHandler: null,
      rating: 4.1,
      mainImg: null,
      showFullDescription: false,
      filtersSelected: [],
      specificCategory: 'renovation-phare-detailing-505',
      reviewsFetchable: false,
      brand: '',
      referenceCopied: false,
    }
  },

  head() {
    if (this.product && this.product.slug) {
      const metaTitle =
        this.product.meta_title +
        (this.product.variant && this.product.variant.name_suffix
          ? ' - ' + this.product.variant.name_suffix
          : '')
      return {
        title: metaTitle,
        link: this.getProductLinkHead(),
        meta: [
          {
            hid: 'description',
            name: 'description',
            content: this.getMetaDescription(
              this.product.meta_description
                ? this.product.meta_description
                : this.product.variant
                ? this.product.variant.description_short
                : ''
            ),
          },
          { hid: 'og:type', property: 'og:type', content: 'website' },
          {
            hid: 'og:url',
            property: 'og:url',
            content:
              this.$config.url +
              this.localePath({
                name: 'produit-slug',
                params: { slug: this.product.slug },
              }),
          },
          {
            hid: 'og:title',
            property: 'og:title',
            content: metaTitle,
          },
          {
            hid: 'og:description',
            property: 'og:description',
            content: this.getMetaDescription(
              this.product.meta_description
                ? this.product.meta_description
                : this.product.variant
                ? this.product.variant.description_short
                : ''
            ),
          },
          {
            hid: 'og:site_name',
            property: 'og:site_name',
            content: 'AGM Vision',
          },
          ...buildOgLocaleMeta(this.$i18n.locale),
          {
            hid: 'og:image',
            property: 'og:image',
            content: this.ogImage.url,
          },
          ...(this.ogImage.width
            ? [
                {
                  hid: 'og:image:width',
                  property: 'og:image:width',
                  content: String(this.ogImage.width),
                },
              ]
            : []),
          ...(this.ogImage.height
            ? [
                {
                  hid: 'og:image:height',
                  property: 'og:image:height',
                  content: String(this.ogImage.height),
                },
              ]
            : []),
          {
            hid: 'og:image:alt',
            property: 'og:image:alt',
            content: metaTitle,
          },
          {
            hid: 'og:price:amount',
            property: 'og:price:amount',
            content: this.product.variant?.price_ttc,
          },
          {
            hid: 'og:price:currency',
            property: 'og:price:currency',
            content: 'EUR',
          },
          {
            hid: 'twitter:card',
            name: 'twitter:card',
            content: 'summary_large_image',
          },
          {
            hid: 'twitter:title',
            name: 'twitter:title',
            content: metaTitle,
          },
          {
            hid: 'twitter:description',
            name: 'twitter:description',
            content: this.getMetaDescription(
              this.product.meta_description
                ? this.product.meta_description
                : this.product.variant
                ? this.product.variant.description_short
                : ''
            ),
          },
          {
            hid: 'twitter:image',
            name: 'twitter:image',
            content: this.ogImage.url,
          },
          {
            hid: 'twitter:image:alt',
            name: 'twitter:image:alt',
            content: metaTitle,
          },
        ],
      }
    }
  },

  computed: {
    featuredSpecificationValues() {
      return withoutColorTemperatureSpecifications(
        this.product.variant.specification_values_featured,
        this.product.variant
      )
    },
    colorTemperatureToneLabel() {
      const wording = colorTemperatureWording(
        this.product.variant.specification_values_featured,
        this.product.variant
      )
      if (wording) return wording

      return this.$t(
        `components.colorTemperature.tones.${colorTemperatureTone(
          this.product.variant.color_temperature
        )}`
      )
    },
    isAdmin() {
      return (
        this.$auth.loggedIn && this.$auth.user?.role?.identifier === 'admin'
      )
    },

    hasSeoDescription() {
      return (
        this.product?.description_top?.length > 10 ||
        this.product?.description_bottom?.length > 10
      )
    },

    locationCode() {
      const variant = this.product?.variant
      if (!variant) return null
      const parts = [
        variant.location_zone,
        variant.location_rack,
        variant.location_level,
      ].filter(Boolean)
      return parts.length ? parts.join(' – ') : null
    },

    ogImage() {
      const image = this.product?.variant?.images?.[0]
      if (!image?.url) {
        return {
          url: `${this.$config.url}/logo_og.png`,
          width: 512,
          height: 512,
        }
      }
      return {
        url: image.url.startsWith('http')
          ? image.url
          : `${this.$config.url}${image.url}`,
        width: image.width || null,
        height: image.height || null,
      }
    },

    getAllMedias() {
      return {
        images: [...this.product.variant.images],
        movies: [...this.product.movies, ...this.product.variant.movies],
        name: this.product.variant.name,
      }
    },
    getQuantityFromVariant() {
      return this.product.variant.variants_items.find(
        item =>
          item.pivot.variant_a_id ===
          parseInt(this.$route.query?.variant ?? this.product.variant.id)
      )?.pivot?.quantity
    },

    quantityDetails() {
      const spec = this.product?.variant?.specification_values?.find(
        s => s.specification?.name === 'Détails des quantités'
      )
      return spec?.value || null
    },

    quantityDetailsShort() {
      const maxLength = this.$device.isMobile ? 35 : 45
      if (!this.quantityDetails) return null
      return this.quantityDetails.length > maxLength
        ? this.quantityDetails.slice(0, maxLength) + '...'
        : this.quantityDetails
    },

    urlToAdminPage() {
      return `${this.$config.adminUrl}/produits/${this.product.id}?variant=${this.product.variant.id}`
    },

    shouldShowCompatibilityWarning() {
      const result =
        this.product.show_excluded_warning &&
        (this.product.vehicles_excluded?.length > 0 ||
          this.product.vehicle_brands_excluded?.length > 0)

      return result
    },

    isH7Product() {
      // Vérifie si le produit appartient à la catégorie H7 (ID 19)
      return (
        this.product?.categories?.some(category => category.id === 19) || false
      )
    },

    productImages() {
      return this.product.variant.images.concat(
        this.product.variant.movies.map(item => {
          return { ...item.image, is_video: true, video: item.video }
        })
      )
    },

    breadcrumb_list() {
      if (!this.product?.slug) {
        return []
      }

      let breadcrumb = [
        {
          text: this.$t('pages.produit.slug.home'),
          to: this.localePath({ name: 'index' }),
        },
      ]

      if (
        this.product.vehicles_included &&
        this.product.vehicles_included.length
      ) {
        breadcrumb.push({
          text: `${this.product.vehicles_included[0].brand.name} ${this.product.vehicles_included[0].name}`,
          to: this.localePath({
            name: 'recherche-led-accessoires-brand-type-typeid',
            params: {
              brand: this.$slugify(
                this.product.vehicles_included[0].brand.name
              ).toLowerCase(),
              type: this.$slugify(
                this.product.vehicles_included[0].name
              ).toLowerCase(),
              typeid: this.product.vehicles_included[0].id,
            },
          }),
        })

        breadcrumb.push({
          text: this.$options.filters.truncate(this.product.name, 30),
          long_text: this.product.name,
          to: this.localePath({
            name: 'produit-slug',
            params: { slug: this.product.slug },
          }),
        })

        return breadcrumb
      }

      if (this.product.categories && this.product.categories.length > 0) {
        let product_subcategory = this.product.categories.filter(
          item => item.count_parents_recursive == 2
        )[0]
        let product_category = product_subcategory?.nested_parents_light
        let product_domain = product_category?.nested_parents_light

        if (
          this.$store.state.breadcrumb.domain &&
          this.product.categories.find(
            item => item.slug == this.$store.state.breadcrumb.domain
          )
        ) {
          breadcrumb.push({
            text: this.product.categories.filter(
              item => item.slug == this.$store.state.breadcrumb.domain
            )[0].name,
            to: this.localePath({
              name: 'categories-domain',
              params: { domain: this.$store.state.breadcrumb.domain },
            }),
          })
        } else if (product_domain) {
          breadcrumb.push({
            text: product_domain.name,
            to: this.localePath({
              name: 'categories-domain',
              params: { domain: product_domain.slug },
            }),
          })
        }

        if (
          this.$store.state.breadcrumb.category &&
          this.product.categories.find(
            item => item.slug == this.$store.state.breadcrumb.category
          )
        ) {
          breadcrumb.push({
            text: this.product.categories.filter(
              item => item.slug == this.$store.state.breadcrumb.category
            )[0].name,
            to: this.localePath({
              name: 'categories-domain-category',
              params: {
                domain: this.$store.state.breadcrumb.domain,
                category: this.$store.state.breadcrumb.category,
              },
            }),
          })
        } else if (product_category && product_domain) {
          breadcrumb.push({
            text: product_category.name,
            to: this.localePath({
              name: 'categories-domain-category',
              params: {
                domain: product_domain.slug,
                category: product_category.slug,
              },
            }),
          })
        }

        if (
          this.$store.state.breadcrumb.subcategory &&
          this.product.categories.find(
            item => item.slug == this.$store.state.breadcrumb.subcategory
          )
        ) {
          breadcrumb.push({
            text: this.product.categories.filter(
              item => item.slug == this.$store.state.breadcrumb.subcategory
            )[0].name,
            to: this.localePath({
              name: 'categories-domain-category-subcategory',
              params: {
                domain: this.$store.state.breadcrumb.domain,
                category: this.$store.state.breadcrumb.category,
                subcategory: this.$store.state.breadcrumb.subcategory,
              },
            }),
          })
        } else if (product_subcategory && product_category && product_domain) {
          breadcrumb.push({
            text: product_subcategory.name,
            to: this.localePath({
              name: 'categories-domain-category-subcategory',
              params: {
                domain: product_domain.slug,
                category: product_category.slug,
                subcategory: product_subcategory.slug,
              },
            }),
          })
        }

        breadcrumb.push({
          text: this.$options.filters.truncate(this.product.name, 30),
          long_text: this.product.name,
          to: this.localePath({
            name: 'produit-slug',
            params: { slug: this.product.slug },
          }),
        })
      } else {
        breadcrumb.push({
          text: this.$options.filters.truncate(this.product.name, 30),
          long_text: this.product.name,
          to: this.localePath({
            name: 'produit-slug',
            params: { slug: this.product.slug },
          }),
        })
      }

      return breadcrumb
    },
  },

  watch: {
    filters_product_selected(newVal, oldVal) {
      if (
        JSON.stringify(newVal) === JSON.stringify(oldVal) ||
        this.$fetchState.pending
      )
        return
      this.$fetch()
    },
  },

  created() {
    if (process.client) {
      this.popStateEventHandler = () => {
        if (!this.$route.query.fv) {
          this.setFiltersProductSelected({})
          this.$store.dispatch('setLastFilterSelected', null)
        }
      }
      window.addEventListener('popstate', this.popStateEventHandler)
    }
  },

  mounted() {
    this.$store.dispatch('breadcrumb/loadVehicleContext')
  },

  beforeDestroy() {
    if (process.client) {
      window.removeEventListener('popstate', this.popStateEventHandler)
    }
  },

  methods: {
    formatColorTemperature,
    async copyReference() {
      if (!(await copyText(this.product.variant.reference))) {
        return
      }
      this.referenceCopied = true
      setTimeout(() => {
        this.referenceCopied = false
      }, 2000)
    },
    getCalcValueFromVariant(variant, valueName) {
      const data = variant?.variants_items?.find(
        item =>
          item.pivot.variant_a_id ===
          parseInt(this.$route.query?.variant ?? this.product.variant.id)
      )
      return variant[valueName] / data?.pivot?.quantity ?? 1
    },
    hasSpecificCategory(categories) {
      for (const category of categories) {
        if (
          category.all_parents.find(item => item.slug === this.specificCategory)
        ) {
          return true
        }
      }
      return false
    },

    getMetaDescription(html) {
      if (!html) return ''
      let text = html.replace(/<[^>]*>/g, '')
      return text.length > 155 ? text.substring(0, 150) + '...' : text
    },

    getVariantName(variant) {
      if (variant.pivot.label) return variant.pivot.label
      if (variant.variant_generic_type)
        return variant.pivot.quantity > 1
          ? variant.variant_generic_type.name_plural
          : variant.variant_generic_type.name_singular
      return variant.name
    },

    showVideo(video) {
      if (process.client) {
        ;(async () => {
          await this.$store.dispatch('setVideo', video)
          this.$bvModal.show('modalVideo')
        })()
      }
    },

    getProductLinkHead() {
      if (!this.product || !this.product.slug) {
        return []
      }

      const link = [
        {
          rel: 'canonical',
          hid: 'i18n-can',
          href:
            this.$config.url +
            this.localePath({
              name: 'produit-slug',
              params: { slug: this.product.slug },
            }),
        },
      ]

      link.push(
        ...buildHreflangLinks({
          locales: this.$i18n.locales,
          configUrl: this.$config.url,
          localePath: this.localePath,
          routeName: 'produit-slug',
          buildParams: code => {
            if ((this.product.restricted_locales || []).includes(code))
              return null
            const slug = entitySlugForLocale(this, this.product, code)
            return slug ? { slug } : null
          },
          store: this.$store,
        })
      )

      const allMovies = [
        ...(this.product.movies || []),
        ...(this.product.variant?.movies || []),
      ]
      const firstMedia =
        allMovies.find(movie => movie.image) ||
        this.product.variant?.images?.[0]
      if (firstMedia) {
        const firstImage = firstMedia.image || firstMedia
        if (firstImage?.url) {
          link.push({
            hid: 'product-image-preload',
            rel: 'preload',
            as: 'image',
            fetchpriority: 'high',
            href: firstImage.url,
          })
        }
      }

      return link
    },

    getUrlCategory(category) {
      if (category.count_parents_recursive == 0) {
        return {
          name: 'categories-domain-category',
          params: { domain: category.slug },
        }
      } else if (
        category.count_parents_recursive == 1 &&
        category.nested_parents_light
      ) {
        return {
          name: 'categories-domain-category',
          params: {
            domain: category.nested_parents_light.slug,
            category: category.slug,
          },
        }
      } else if (
        category.count_parents_recursive == 2 &&
        category.nested_parents
      ) {
        return {
          name: 'categories-domain-category-subcategory',
          params: {
            domain: category.nested_parents.nested_parents.slug,
            category: category.nested_parents.slug,
            subcategory: category.slug,
          },
        }
      } else {
        return {}
      }
    },

    slugToBreadcrumb(slug) {
      let breadcrumb = slug.split('-')
      breadcrumb.pop()
      return breadcrumb.join().replace(/,/g, ' ')
    },

    labelArray(variants_items) {
      return [
        ...new Set(variants_items.map(item => item.pivot.label || item.name)),
      ]
    },

    showImage(id) {
      if (process.client) {
        document.getElementsByClassName(`image-${id}`)[0].click()
      }
    },

    moveToTab(tab) {
      this.tabIndex = tab
    },

    getReviewsTabIndex() {
      let index = 0
      if (
        this.product.variant.description_long_subtitle ||
        this.product.variant.description_long_title ||
        this.product.variant.descriptions.length
      ) {
        index++
      }
      if (
        this.product.variant.specification_values &&
        this.product.variant.specification_values.length > 0
      ) {
        index++
      }
      return index
    },

    showReviewsSection() {
      this.moveToReviewsTab()
      const scrollTo = element =>
        window.scrollTo({
          top: element.getBoundingClientRect().top + window.scrollY - 90,
          behavior: 'smooth',
        })
      let attempts = 0
      const waitForStrip = () => {
        const strip = document.getElementById('photos-videos-clients')
        if (strip) {
          scrollTo(strip)
          return
        }
        attempts++
        if (attempts === 1 && this.$refs.productTabs?.$el) {
          scrollTo(this.$refs.productTabs.$el)
        }
        if (attempts < 40) setTimeout(waitForStrip, 100)
      }
      this.$nextTick(waitForStrip)
    },
    moveToReviewsTab() {
      const index = this.getReviewsTabIndex()
      this.reviewsFetchable = true
      this.moveToTab(index)
    },

    moveToCompatibilityTab() {
      let index = this.getReviewsTabIndex()
      if (this.product.published_reviews) {
        index++
      }
      if (
        this.product.variant.documentations &&
        this.product.variant.documentations.length > 0
      ) {
        index++
      }
      if (this.product.questions && this.product.questions.length > 0) {
        index++
      }

      this.moveToTab(index)

      this.$nextTick(() => {
        const tabsElement = document.getElementById('tabsProduct')
        if (tabsElement) {
          tabsElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },
  },
}
